import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api, CATEGORIES, CATEGORY_LABEL, resolvePhotoUrl } from "../lib/api";
import SEO from "../components/site/SEO";

const HERO_IMG = "https://images.unsplash.com/photo-1485291571150-772bcfc10da5?auto=format&fit=crop&w=2400&q=80";

export default function Home() {
    const [photos, setPhotos] = React.useState([]);

    React.useEffect(() => {
        api.get("/photos").then((r) => setPhotos(r.data)).catch(() => {});
    }, []);

    const featured = photos.slice(0, 6);

    return (
        <div data-testid="page-home">
            <SEO title={null} description="Photographe automobile et lifestyle. Mariages, baptêmes, portraits. Style cinématographique, livraison soignée." />
            {/* Hero */}
            <section className="relative h-screen w-full overflow-hidden">
                <img
                    src={HERO_IMG}
                    alt="Cinematic"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/50 to-black" />
                <div className="relative z-10 h-full flex flex-col justify-end pb-24 px-6 md:px-12 lg:px-24">
                    <p
                        data-testid="hero-eyebrow"
                        className="text-xs uppercase tracking-[0.4em] text-[#c5a059] mb-6"
                    >
                        photographe automobile & lifestyle
                    </p>
                    <h1
                        data-testid="hero-title"
                        className="font-serif-display text-5xl sm:text-7xl lg:text-8xl text-white max-w-5xl leading-[0.95]"
                    >
                        L'instant qui<br />
                        <em className="italic text-[#c5a059]">ne reviendra</em> pas.
                    </h1>
                    <div className="mt-10 flex flex-col sm:flex-row gap-4">
                        <Link
                            to="/galerie"
                            data-testid="hero-cta-galerie"
                            className="bg-[#c5a059] text-black font-medium px-8 py-4 rounded-none hover:bg-[#dcc082] transition-colors inline-flex items-center gap-2 uppercase tracking-[0.18em] text-sm"
                        >
                            Voir les galeries <ArrowUpRight size={16} strokeWidth={1.5} />
                        </Link>
                        <Link
                            to="/contact"
                            data-testid="hero-cta-contact"
                            className="border border-white/30 text-white px-8 py-4 rounded-none hover:bg-white hover:text-black transition-colors uppercase tracking-[0.18em] text-sm"
                        >
                            Demander un devis
                        </Link>
                    </div>
                </div>
            </section>

            {/* Categories */}
            <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
                <div className="flex items-end justify-between mb-16 flex-wrap gap-6">
                    <div>
                        <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">— 01 / Spécialités</p>
                        <h2 className="font-serif-display text-4xl md:text-5xl">Quatre univers,<br />un même regard.</h2>
                    </div>
                    <Link to="/galerie" data-testid="home-galleries-link" className="text-[#c5a059] uppercase tracking-[0.2em] text-xs hover:underline">
                        Tout voir →
                    </Link>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {CATEGORIES.map((c, i) => {
                        const sample = photos.find((p) => p.category === c.key);
                        return (
                            <Link
                                key={c.key}
                                to={`/galerie?cat=${c.key}`}
                                data-testid={`home-cat-${c.key}`}
                                className="photo-tile relative aspect-[3/4] overflow-hidden border border-white/10 group block"
                            >
                                {sample ? (
                                    <img src={resolvePhotoUrl(sample.url)} alt={c.label} className="w-full h-full object-cover" loading="lazy" />
                                ) : (
                                    <div className="w-full h-full bg-zinc-900" />
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                                <div className="absolute bottom-0 left-0 right-0 p-6">
                                    <p className="text-xs uppercase tracking-[0.3em] text-[#c5a059]">0{i + 1}</p>
                                    <h3 className="font-serif-display text-3xl text-white mt-2">{c.label}</h3>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </section>

            {/* Featured strip */}
            {featured.length > 0 && (
                <section className="px-6 md:px-12 lg:px-24 py-16 border-t border-white/10">
                    <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">— 02 / Sélection</p>
                    <h2 className="font-serif-display text-4xl md:text-5xl mb-12">Récents travaux</h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6">
                        {featured.map((p) => (
                            <div key={p.id} className="photo-tile aspect-square overflow-hidden border border-white/5">
                                <img src={resolvePhotoUrl(p.url)} alt={p.title || ""} className="w-full h-full object-cover" loading="lazy" />
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Quote band */}
            <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32 border-t border-white/10">
                <div className="max-w-4xl">
                    <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-6">— Manifeste</p>
                    <p className="font-serif-display text-3xl md:text-5xl leading-tight">
                        “Une bonne photo, c'est une histoire qui n'a pas besoin de mots —
                        <em className="text-[#c5a059] italic"> juste de lumière, et d'un peu de patience.</em>”
                    </p>
                    <p className="mt-8 text-sm uppercase tracking-[0.25em] text-zinc-500">— Paul</p>
                </div>
            </section>
        </div>
    );
}
